import { useEffect, useMemo, useState, useCallback } from "react";
import {
  fetchAgentQuestions,
  createAgentQuestion,
  updateAgentQuestion,
  deleteAgentQuestion,
  fetchDefaultAgentQuestions,
  saveDefaultAgentQuestions,
  fetchAgentPurposes,
} from "@services/agentQuestions";
import { sanitizeFormValues } from "@utils/sanitize";

export function useAdminAgentQuestions() {
  const [tab, setTab] = useState("LIBRARY");

  const [library, setLibrary] = useState([]);
  const [query, setQuery] = useState("");

  const [purposes, setPurposes] = useState([]);
  const [purpose, setPurpose] = useState(null);

  const [defaultFlow, setDefaultFlow] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState(null);
  const clearError = () => setError(null);

  const refreshLibrary = useCallback(async () => {
    const res = await fetchAgentQuestions();
    setLibrary(res?.data || []);
  }, []);

  const refreshPurposes = useCallback(async () => {
    const res = await fetchAgentPurposes();
    // Backend returns [{ value, is_optional }, ...]; older shape was plain
    // strings. Normalize to string values — the library/default-flow UI keys
    // and renders purposes as strings. (Mirrors getPurposeValues in
    // useAdminClinics.js.)
    const list = (res?.data || []).map((p) =>
      typeof p === "string" ? p : p?.value
    ).filter(Boolean);
    setPurposes(list);
    return list;
  }, []);

  const refreshDefaults = useCallback(async (p) => {
    if (!p) return;
    const res = await fetchDefaultAgentQuestions({ purpose: p });
    setDefaultFlow(res?.data || []);
  }, []);

  // Initial load: purposes + library only (NO defaults here)
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const list = await refreshPurposes();
        const initialPurpose = list?.[0] || null;
        setPurpose(initialPurpose);

        await refreshLibrary();
        clearError();
      } catch (e) {
        setError({
          scope: "global",
          message: e?.message || "Failed to load agent questions",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshLibrary, refreshPurposes]);

  // Load defaults ONLY when on DEFAULTS tab and purpose exists
  useEffect(() => {
    (async () => {
      try {
        if (tab !== "DEFAULTS") return;
        if (!purpose) return;
        await refreshDefaults(purpose);
      } catch (e) {
        setError({
          scope: "defaults",
          message: e?.message || "Failed to load default flow",
        });
      }
    })();
  }, [tab, purpose, refreshDefaults]);

  const filteredLibrary = useMemo(() => {
    if (!query) return library;
    const q = query.toLowerCase();
    return library.filter(
      (x) =>
        (x.agent_question_enum || "").toLowerCase().includes(q) ||
        (x.question || "").toLowerCase().includes(q)
    );
  }, [library, query]);

  // DefaultFlowManager uses libraryByEnum.get(...)
  const libraryByEnum = useMemo(() => {
    const map = new Map();
    library.forEach((x) => map.set(x.agent_question_enum, x));
    return map;
  }, [library]);

  const createQuestion = async (payload) => {
    setSaving(true);
    try {
      await createAgentQuestion(sanitizeFormValues(payload));
      await refreshLibrary();
      clearError();
      return true;
    } catch (e) {
      setError({
        scope: "create",
        message: e?.message || "Failed to create question",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = async (questionEnum, patch) => {
    setSaving(true);
    try {
      await updateAgentQuestion(questionEnum, sanitizeFormValues(patch));
      await refreshLibrary();
      clearError();
    } catch (e) {
      setError({
        scope: "update",
        message: e?.message || "Failed to update question",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteQuestion = async (questionEnum) => {
    setSaving(true);
    try {
      await deleteAgentQuestion(questionEnum);
      await refreshLibrary();
      clearError();
    } catch (e) {
      setError({
        scope: "delete",
        message: e?.message || "Failed to delete question",
      });
    } finally {
      setSaving(false);
    }
  };

  // ✅ explicit save (called only by Save button)
  const saveFlow = async (items) => {
    if (!purpose) {
      setError({
        scope: "defaults",
        message: "Please select a purpose first.",
      });
      return;
    }

    setSaving(true);
    try {
      await saveDefaultAgentQuestions(sanitizeFormValues({ purpose, items }));
      await refreshDefaults(purpose);
      clearError();
    } catch (e) {
      setError({
        scope: "defaults",
        message: e?.message || "Failed to save default flow",
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    PURPOSES: purposes,

    tab,
    setTab,

    query,
    setQuery,
    filteredLibrary,
    library,
    libraryByEnum,

    purpose,
    setPurpose,

    defaultFlow,
    saveFlow,

    loading,
    saving,

    error,
    clearError,

    createQuestion,
    updateQuestion,
    deleteQuestion,

    refreshLibrary,
    refreshDefaults,
  };
}
